import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import { getMyCourses, getMyCertificates } from '../../services/lmsService';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import Footer from '../Footer';
import {
  BookOpen, Calendar, Award, Clock, PlayCircle,
  ChevronRight, GraduationCap, TrendingUp, CheckCircle,
  Video, BarChart3, Download, ExternalLink, RefreshCw
} from 'lucide-react';

const MyLearning = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [enrollments, setEnrollments] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [summary, setSummary] = useState({ total_enrolled: 0, in_progress: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('in-progress');
  
  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [coursesRes, certsRes] = await Promise.all([
        getMyCourses(),
        getMyCertificates().catch(() => ({ data: { certificates: [] } }))
      ]);
      
      if (coursesRes.success) {
        setEnrollments(coursesRes.data.enrollments);
        setSummary(coursesRes.data.summary);
      }
      
      if (certsRes.success || certsRes.data) {
        setCertificates(certsRes.data?.certificates || []);
      }
    } catch (error) {
      console.error('Failed to fetch learning data:', error);
      toast.error('Failed to load your courses');
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-blue-100 text-blue-700">In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
      case 'dropped':
        return <Badge className="bg-red-100 text-red-700">Dropped</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };
  
  const filterEnrollments = (status) => {
    if (status === 'all') return enrollments;
    if (status === 'in-progress') return enrollments.filter(e => e.status === 'active');
    if (status === 'completed') return enrollments.filter(e => e.status === 'completed');
    return enrollments;
  };
  
  const calculateProgress = (progress) => {
    if (!progress) return 0;
    const attendance = progress.attendance_percent || 0;
    const sessions = progress.sessions_attended ? (progress.sessions_attended / (progress.total_sessions || 1)) * 100 : 0;
    return Math.round((attendance + sessions) / 2);
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <GraduationCap className="h-7 w-7 sm:h-8 sm:w-8 text-indigo-600" />
                  My Learning
                </h1>
                <p className="text-gray-600 text-sm sm:text-base">Track your progress and continue learning</p>
              </div>
              <Button 
                className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
                onClick={() => navigate('/courses')}
              >
                Browse Courses
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100">
                <CardContent className="p-4 flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-indigo-100 rounded-lg">
                    <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{summary.total_enrolled}</p>
                    <p className="text-xs sm:text-sm text-gray-600">Total Courses</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-100">
                <CardContent className="p-4 flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{summary.in_progress}</p>
                    <p className="text-xs sm:text-sm text-gray-600">In Progress</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-100">
                <CardContent className="p-4 flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{summary.completed}</p>
                    <p className="text-xs sm:text-sm text-gray-600">Completed</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4 sm:mb-6 w-full sm:w-auto grid grid-cols-3 sm:flex">
              <TabsTrigger value="in-progress" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <PlayCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">In Progress</span>
                <span className="sm:hidden">Active</span>
                <span className="text-xs">({summary.in_progress})</span>
              </TabsTrigger>
              <TabsTrigger value="completed" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Completed</span>
                <span className="sm:hidden">Done</span>
                <span className="text-xs">({summary.completed})</span>
              </TabsTrigger>
              <TabsTrigger value="certificates" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <Award className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Certificates</span>
                <span className="sm:hidden">Certs</span>
                <span className="text-xs">({certificates.length})</span>
              </TabsTrigger>
            </TabsList>
            
            {/* In Progress Tab */}
            <TabsContent value="in-progress">
              {filterEnrollments('in-progress').length === 0 ? (
                <Card className="text-center py-8 sm:py-12">
                  <CardContent>
                    <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-600 mb-2">No courses in progress</h3>
                    <p className="text-gray-500 text-sm sm:text-base mb-4">Start learning by enrolling in a course</p>
                    <Button 
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => navigate('/courses')}
                    >
                      Browse Courses
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {filterEnrollments('in-progress').map((enrollment) => (
                    <Card key={enrollment.enrollment_id} className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-base sm:text-lg text-gray-900 mb-1 truncate">
                              {enrollment.course_title}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-500">
                              Batch: {enrollment.batch_name}
                            </p>
                          </div>
                          {getStatusBadge(enrollment.status)}
                        </div>
                        
                        {/* Progress */}
                        <div className="mb-4">
                          <div className="flex justify-between text-xs sm:text-sm mb-1">
                            <span className="text-gray-600">Progress</span>
                            <span className="font-medium">{calculateProgress(enrollment.progress)}%</span>
                          </div>
                          <Progress value={calculateProgress(enrollment.progress)} className="h-2" />
                        </div>
                        
                        {/* Stats */}
                        <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500 mb-4">
                          <div className="flex items-center gap-1">
                            <Video className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span>{enrollment.progress?.sessions_attended || 0}/{enrollment.progress?.total_sessions || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span>{enrollment.progress?.attendance_percent || 0}%</span>
                          </div>
                        </div>
                        
                        {/* Next Session */}
                        {enrollment.next_session && (
                          <div className="p-2 sm:p-3 bg-indigo-50 rounded-lg mb-4">
                            <p className="text-xs sm:text-sm font-medium text-indigo-800">
                              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                              Next: {enrollment.next_session.title}
                            </p>
                            <p className="text-xs text-indigo-600">
                              {new Date(enrollment.next_session.scheduled_date).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          <Button 
                            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm"
                            onClick={() => navigate(`/my-learning/${enrollment.enrollment_id}`)}
                          >
                            Continue
                            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                          </Button>
                          <Button 
                            variant="outline"
                            size="icon"
                            onClick={() => navigate('/calendar')}
                          >
                            <Calendar className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            
            {/* Completed Tab */}
            <TabsContent value="completed">
              {filterEnrollments('completed').length === 0 ? (
                <Card className="text-center py-8 sm:py-12">
                  <CardContent>
                    <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-600 mb-2">No completed courses yet</h3>
                    <p className="text-gray-500 text-sm">Complete your in-progress courses to see them here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {filterEnrollments('completed').map((enrollment) => (
                    <Card key={enrollment.enrollment_id} className="border-green-200 bg-green-50/30">
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-bold text-base sm:text-lg text-gray-900 mb-1">
                              {enrollment.course_title}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-500">
                              Completed: {new Date(enrollment.completed_at).toLocaleDateString()}
                            </p>
                          </div>
                          <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
                        </div>
                        
                        <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500 mb-4">
                          <div className="flex items-center gap-1">
                            <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span>{enrollment.progress?.attendance_percent || 100}% attendance</span>
                          </div>
                          {enrollment.certificate_id && (
                            <div className="flex items-center gap-1 text-green-600">
                              <Award className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span>Certificate earned</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            variant="outline"
                            className="flex-1 text-xs sm:text-sm"
                            onClick={() => navigate(`/my-learning/${enrollment.enrollment_id}`)}
                          >
                            Recordings
                            <PlayCircle className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                          </Button>
                          {enrollment.certificate_id && (
                            <Button 
                              className="bg-green-600 hover:bg-green-700"
                              size="icon"
                              onClick={() => navigate(`/certificates/${enrollment.certificate_id}`)}
                            >
                              <Award className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            
            {/* Certificates Tab */}
            <TabsContent value="certificates">
              {certificates.length === 0 ? (
                <Card className="text-center py-8 sm:py-12">
                  <CardContent>
                    <Award className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-600 mb-2">No certificates yet</h3>
                    <p className="text-gray-500 text-sm">Complete courses to earn certificates</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {certificates.map((cert) => (
                    <Card key={cert.id} className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                      <CardContent className="p-4 sm:p-6">
                        <div className="text-center mb-4">
                          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mb-3">
                            <Award className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                          </div>
                          <h3 className="font-bold text-sm sm:text-lg text-gray-900 line-clamp-2">{cert.course_title}</h3>
                          <p className="text-xs sm:text-sm text-gray-500">#{cert.certificate_number}</p>
                        </div>
                        
                        <div className="text-center text-xs sm:text-sm text-gray-500 mb-4">
                          <p>Issued: {new Date(cert.issued_at).toLocaleDateString()}</p>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            variant="outline"
                            className="flex-1 text-xs sm:text-sm"
                            onClick={() => navigate(`/certificates/${cert.id}`)}
                          >
                            View
                            <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                          </Button>
                          {cert.pdf_url && (
                            <Button 
                              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm"
                              onClick={() => window.open(cert.pdf_url, '_blank')}
                            >
                              Download
                              <Download className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default MyLearning;
